import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CompanyAuditFilters from './CompanyAuditFilters';
import CompanyAuditRow from './CompanyAuditRow';
import PaginationFooter from '../../../admin/components/PaginationFooter';

const HEADERS = [
  { label: 'Data', align: 'left' },
  { label: 'Usuário', align: 'left' },
  { label: 'Ação', align: 'left' },
  { label: 'Paciente', align: 'left', patientOnly: true },
  { label: 'IP', align: 'left' },
  { label: 'Detalhes', align: 'left' },
];

/**
 * CompanyAuditTable - Filtros, tabela e paginação server-side da Auditoria da clínica
 * (LGPD L-14). Mesmo padrão de AuditTable (Admin Geral > Auditoria).
 * @param {Object} props
 * @param {Array} props.entries
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {Object} props.pagination
 * @param {Object} props.filters - props repassadas ao CompanyAuditFilters
 * @param {Function} props.onPageChange
 * @param {boolean} props.hidePatientColumn - true quando já filtrado por um paciente (atalho da ficha)
 */
function CompanyAuditTable({
  entries = [],
  isLoading = false,
  error = null,
  pagination,
  filters,
  onPageChange,
  hidePatientColumn = false,
}) {
  const hasActiveFilters = Boolean(filters?.action || filters?.userId || filters?.dateFrom || filters?.dateTo);
  const headers = HEADERS.filter((h) => !(hidePatientColumn && h.patientOnly));

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
      }}
    >
      <CompanyAuditFilters {...filters} />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {headers.map((h) => (
                <TableCell
                  key={h.label}
                  align={h.align}
                  sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveFilters ? 'Nenhum registro com esses filtros' : 'Nenhum registro de auditoria'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <CompanyAuditRow key={entry.id} entry={entry} hidePatientColumn={hidePatientColumn} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationFooter pagination={pagination} onPageChange={onPageChange} itemLabel="registros" />
    </Paper>
  );
}

export default CompanyAuditTable;
